<template>
  <div class="w-full space-y-4 text-slate-100">
    <!-- TOP BAR -->
    <div
      class="min-w-0 border border-slate-700 bg-slate-800 bg-opacity-80 shadow-lg"
      style="border-radius: 26px;"
    >
      <div class="flex flex-col gap-4 px-4 py-4 sm:px-5 sm:py-5">
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <!-- LEFT -->
          <div class="min-w-0">
            <nav class="hidden sm:flex" aria-label="Breadcrumb">
              <ol role="list" class="flex items-center gap-2 text-xs text-slate-400">
                <li class="flex items-center">
                  <router-link
                    to="/hub"
                    class="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-slate-600 bg-slate-700 bg-opacity-70 text-slate-200 transition hover:bg-slate-600"
                  >
                    <HomeIcon class="h-4 w-4" aria-hidden="true" />
                    <span class="sr-only">Home</span>
                  </router-link>
                </li>

                <li class="flex items-center gap-2">
                  <svg
                    class="h-4 w-4 text-slate-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                  <span class="font-medium text-slate-300">Flota</span>
                </li>
              </ol>
            </nav>

            <div class="flex items-center sm:hidden">
              <router-link
                to="/hub"
                class="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-700 px-3 py-2 text-sm font-medium text-slate-200 transition hover:bg-slate-600"
              >
                &larr; Cofnij
              </router-link>
            </div>

            <div class="mt-3">
              <div class="text-lg font-semibold tracking-tight text-white">
                Zarządzanie flotą
              </div>
              <div class="mt-1 text-xs text-slate-400">
                Lista pojazdów i naczep wraz z możliwością podglądu i zarządzania.
              </div>
            </div>
          </div>

          <!-- RIGHT -->
          <div class="flex shrink-0 items-center">
            <button
              type="button"
              @click.prevent="showAddModal = true"
              class="inline-flex items-center gap-2 rounded-2xl border border-slate-600 bg-slate-700 px-4 py-2.5 text-xs font-semibold text-slate-100 shadow-sm transition hover:bg-slate-600"
            >
              <svg
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 5v14M5 12h14" />
              </svg>
              Dodaj {{ showVehicles ? 'pojazd' : 'naczepę' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- TABLE CARD -->
    <div
      class="border border-slate-700 bg-slate-800 bg-opacity-80 shadow-lg"
      style="border-radius: 26px;"
    >
      <div class="border-b border-slate-700 px-4 py-4 sm:px-5">
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div class="min-w-0">
            <div class="text-sm font-semibold text-white">
              {{ showVehicles ? 'Pojazdy' : 'Naczepy' }}
            </div>
            <div class="mt-1 text-xs text-slate-400">
              Łącznie pozycji: {{ sortedFleet.length }}
            </div>
          </div>

          <!-- SWITCH -->
          <div class="shrink-0">
            <nav
              class="inline-flex rounded-2xl border border-slate-700 bg-slate-900 bg-opacity-50 p-1 shadow-inner"
              aria-label="Tabs"
            >
              <button
                type="button"
                @click.prevent="changeList('vehicles')"
                :class="[
                  showVehicles
                    ? 'bg-slate-700 text-white shadow'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800',
                  'rounded-xl px-4 py-2 text-sm font-semibold transition'
                ]"
              >
                Pojazdy
              </button>

              <button
                type="button"
                @click.prevent="changeList('trailers')"
                :class="[
                  !showVehicles
                    ? 'bg-slate-700 text-white shadow'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800',
                  'rounded-xl px-4 py-2 text-sm font-semibold transition'
                ]"
              >
                Naczepy
              </button>
            </nav>
          </div>
        </div>
      </div>

      <div class="px-4 py-4 sm:px-5">
        <div v-if="!queryLoading" class="overflow-hidden rounded-2xl border border-slate-700">
          <div class="w-full min-w-0 overflow-hidden">
            <table class="w-full divide-y divide-slate-700 fleetTable" style="table-layout: fixed;">
              <thead class="bg-slate-700 bg-opacity-70">
                <tr>
                  <th
                    scope="col"
                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold whitespace-nowrap text-slate-100 sm:pl-6"
                    style="width: 80px;"
                  >
                    <div class="flex items-center gap-1">
                      ID
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === 'id' && sort.type === 'asc'"
                        @click.prevent="setSort('id','desc')"
                        :class="iconCls('id')"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('id','asc')"
                        :class="iconCls('id')"
                      />
                    </div>
                  </th>

                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold whitespace-nowrap text-slate-100"
                    style="width: 160px;"
                  >
                    <div class="flex items-center gap-1">
                      Rejestracja
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === 'licensePlate' && sort.type === 'asc'"
                        @click.prevent="setSort('licensePlate','desc')"
                        :class="iconCls('licensePlate')"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('licensePlate','asc')"
                        :class="iconCls('licensePlate')"
                      />
                    </div>
                  </th>

                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-slate-100"
                    style="width: 170px;"
                  >
                    <div class="flex items-center gap-1">
                      Marka
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === 'brand' && sort.type === 'asc'"
                        @click.prevent="setSort('brand','desc')"
                        :class="iconCls('brand')"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('brand','asc')"
                        :class="iconCls('brand')"
                      />
                    </div>
                  </th>

                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold text-slate-100"
                    style="width: 170px;"
                  >
                    <div class="flex items-center gap-1">
                      Model
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === 'model' && sort.type === 'asc'"
                        @click.prevent="setSort('model','desc')"
                        :class="iconCls('model')"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('model','asc')"
                        :class="iconCls('model')"
                      />
                    </div>
                  </th>

                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold whitespace-nowrap text-slate-100"
                    style="width: 165px;"
                  >
                    <div class="flex items-center gap-1">
                      Poj. zbiornika (L)
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === 'tankLitreCapacity' && sort.type === 'asc'"
                        @click.prevent="setSort('tankLitreCapacity','desc')"
                        :class="iconCls('tankLitreCapacity')"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('tankLitreCapacity','asc')"
                        :class="iconCls('tankLitreCapacity')"
                      />
                    </div>
                  </th>

                  <th
                    scope="col"
                    class="px-3 py-3.5 text-left text-sm font-semibold whitespace-nowrap text-slate-100"
                    style="width: 100px;"
                  >
                    <div class="flex items-center gap-1">
                      Aktywny
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === 'active' && sort.type === 'asc'"
                        @click.prevent="setSort('active','desc')"
                        :class="iconCls('active')"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('active','asc')"
                        :class="iconCls('active')"
                      />
                    </div>
                  </th>

                  <th
                    scope="col"
                    class="relative py-3.5 pl-3 pr-4 sm:pr-6"
                    style="width: 72px;"
                  >
                    <span class="sr-only">Akcje</span>
                  </th>
                </tr>
              </thead>

              <tbody class="divide-y divide-slate-700 bg-slate-800 bg-opacity-40">
                <tr
                  v-for="row in sortedFleet"
                  :key="row.id"
                  class="transition hover:bg-slate-700 hover:bg-opacity-30"
                >
                  <td class="whitespace-nowrap py-4 pl-4 pr-3 text-xs sm:pl-6">
                    <div class="flex items-center text-xs text-slate-100">
                      <span
                        v-tooltip="`${row.id}`"
                        @click.prevent="copyToClipboard(row.id)"
                        class="cursor-pointer font-semibold text-blue-300 transition hover:text-blue-200"
                      >
                        Kopiuj
                      </span>
                    </div>
                  </td>

                  <td class="px-3 py-4 text-xs text-slate-100 font-medium">
                    <div class="truncate" style="max-width: 160px;">
                      {{ row.licensePlate || '--' }}
                    </div>
                  </td>

                  <td class="px-3 py-4 text-xs text-slate-100 font-medium">
                    <div class="truncate" style="max-width: 170px;">
                      {{ row.brand || '--' }}
                    </div>
                  </td>

                  <td class="px-3 py-4 text-xs text-slate-100 font-medium">
                    <div class="truncate" style="max-width: 170px;">
                      {{ row.model || '--' }}
                    </div>
                  </td>

                  <td class="whitespace-nowrap px-3 py-4 text-xs text-slate-100 font-medium">
                    {{ row.tankLitreCapacity != null ? row.tankLitreCapacity : '--' }}
                  </td>

                  <td class="whitespace-nowrap px-3 py-4 text-xs text-slate-100 font-medium">
                    <span :class="[row.active ? 'text-green-300' : 'text-red-300']">
                      {{ row.active ? 'TAK' : 'NIE' }}
                    </span>
                  </td>

                  <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 select-none">
                    <div class="flex items-center justify-end gap-2">
                      <button
                        v-tooltip="`Zarządzaj`"
                        type="button"
                        @click.prevent="manageRow(row)"
                        class="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-600 bg-slate-700 bg-opacity-80 transition hover:bg-slate-600"
                      >
                        <PencilSquareIcon class="h-4 w-4 text-slate-200" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <EmptyState v-if="fleetList.length === 0"></EmptyState>
          </div>
        </div>

        <div v-else class="flex items-center justify-center py-10">
          <div class="spinner"></div>
        </div>

        <div class="mt-5 flex items-center justify-center" v-if="!queryLimitReached && !queryLoading">
          <button
            type="button"
            @click.prevent="loadMore(10)"
            class="inline-flex items-center rounded-2xl border border-slate-600 bg-slate-700 px-4 py-2.5 text-sm font-semibold text-slate-100 transition hover:bg-slate-600"
          >
            Załaduj więcej
          </button>
        </div>
      </div>
    </div>

    <!-- Modale -->
    <ModalNewFleet
      v-if="showAddModal"
      :show="showAddModal"
      @closeClientModal="showAddModal = false"
      :showVehicles="showVehicles"
    />

    <ModalManageFleet
      :show="showManageFleet"
      :managedFleet="pickedFleet"
      @reloadFleet="handleReloadFleet"
      @closeFleetModal="showManageFleet = false"
    />
  </div>
</template>

<script>
import { HomeIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/24/solid'
import { PencilSquareIcon } from '@heroicons/vue/24/outline'
import EmptyState from '@/components/EmptyState.vue'
import { db } from '@/firebase/gfbconf.js'
import ModalNewFleet from '@/components/ModalNewFleet.vue'
import ModalManageFleet from '@/components/ModalManageFleet.vue'

export default {
  name: 'ManageFleet',
  components: {
    EmptyState,
    HomeIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    PencilSquareIcon,
    ModalNewFleet,
    ModalManageFleet
  },
  data() {
    return {
      showManageFleet: false,
      pickedFleet: null,
      showVehicles: true,
      showAddModal: false,
      queryLoading: false,
      queryLimitReached: false,
      dbListener: null,
      fleetList: [],
      queryLimit: 50,
      sort: {
        pickedColumn: 'licensePlate',
        type: 'asc'
      }
    }
  },

  created() {
    if (!this.$store.state.userData.permissions.admin) {
      this.$router.push('/hub')
      return
    }
    this.downloadFleet()
  },

  computed: {
    sortedFleet() {
      const list = [...this.fleetList]
      const col = this.sort.pickedColumn
      const dir = this.sort.type === 'asc' ? 1 : -1

      return list.sort((a, b) => {
        const va = a?.[col]
        const vb = b?.[col]

        if (col === 'tankLitreCapacity') {
          const na = Number.isFinite(+va) ? +va : -Infinity
          const nb = Number.isFinite(+vb) ? +vb : -Infinity
          return (na - nb) * dir
        }

        if (col === 'active') {
          return (((va ? 1 : 0) - (vb ? 1 : 0)) * dir)
        }

        const sa = (va ?? '').toString().toLowerCase()
        const sb = (vb ?? '').toString().toLowerCase()
        if (sa < sb) return -1 * dir
        if (sa > sb) return 1 * dir
        return 0
      })
    }
  },

  methods: {
    iconCls(col) {
      return {
        'h-4 w-4 cursor-pointer transition hover:text-blue-300': true,
        'opacity-30': this.sort.pickedColumn !== col,
        'text-slate-300': true
      }
    },

    setSort(column, type) {
      this.sort.pickedColumn = column
      this.sort.type = type
    },

    manageRow(row) {
      this.pickedFleet = row
      this.showManageFleet = true
    },

    handleReloadFleet() {
      if (this.dbListener) this.dbListener()
      this.downloadFleet()
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$store.commit('setNotification', {
          show: true,
          head: 'Skopiowano do schowka!',
          subheader: ``,
          success: true
        })
      })
    },

    changeList(type) {
      const target = type === 'vehicles'
      if (this.showVehicles !== target) {
        this.showVehicles = target
        this.queryLimit = 50
        if (this.dbListener) this.dbListener()
        this.downloadFleet()
      }
    },

    downloadFleet() {
      this.queryLoading = true

      const query = db
        .collection('Fleet')
        .where('vehicle', '==', this.showVehicles)
        .limit(this.queryLimit)

      this.dbListener = query.onSnapshot((qs) => {
        const next = []
        qs.forEach((doc) => {
          const data = doc.data()
          next.push({ id: doc.id, ...data })
        })
        this.fleetList = next
        this.queryLimitReached = qs.docs.length < this.queryLimit
        this.queryLoading = false
      }, () => {
        this.queryLoading = false
      })
    },

    loadMore(amount) {
      if (this.dbListener) this.dbListener()
      this.queryLimit = this.queryLimit + amount
      this.downloadFleet()
    }
  },

  beforeUnmount() {
    if (this.dbListener) this.dbListener()
  }
}
</script>

<style lang="scss" scoped>
.spinner {
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  border: 5px solid #475569;
  animation: spinner-bulqg1 1.3s infinite linear alternate, spinner-oaa3wk 2.6s infinite linear;
}

@keyframes spinner-bulqg1 {
  0% { clip-path: polygon(50% 50%, 0 0, 50% 0%, 50% 0%, 50% 0%, 50% 0%, 50% 0%); }
  12.5% { clip-path: polygon(50% 50%, 0 0, 50% 0%, 100% 0%, 100% 0%, 100% 0%, 100% 0%); }
  25% { clip-path: polygon(50% 50%, 0 0, 50% 0%, 100% 0%, 100% 100%, 100% 100%, 100% 100%); }
  50% { clip-path: polygon(50% 50%, 0 0, 50% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%); }
  62.5% { clip-path: polygon(50% 50%, 100% 0, 100% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%); }
  75% { clip-path: polygon(50% 50%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 50% 100%, 0% 100%); }
  100% { clip-path: polygon(50% 50%, 50% 100%, 50% 100%, 50% 100%, 50% 100%, 50% 100%, 0% 100%); }
}

@keyframes spinner-oaa3wk {
  0% { transform: scaleY(1) rotate(0deg); }
  49.99% { transform: scaleY(1) rotate(135deg); }
  50% { transform: scaleY(-1) rotate(0deg); }
  100% { transform: scaleY(-1) rotate(-135deg); }
}

.fleetTable th,
.fleetTable td {
  overflow: hidden;
}

.fleetTable th > div,
.fleetTable td > div {
  min-width: 0;
}
</style>